import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 5.2 visual preview at /preview/lesson/5.2
 * "The ask email: writing to a teacher, professor, or advisor"
 * Vocab: Grounding.
 */

export default function LessonPreviewPage5_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 5 · Taking the chain into the wild · Lesson 5.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The ask email: writing to a teacher, professor, or advisor
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Grounding · Specifics · Ask</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 grounding-line drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two emails to the same professor. One gets a rec letter.">
      <p className="mt-6 text-text/90">
        Two students in the same section email the professor the same week
        asking for a college recommendation. Same professor. Same class. Same
        November deadline. Both used AI to clean up the draft. Here is the
        opening of each.
      </p>

      <div className="mt-6 space-y-4">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Email A · Archived without a reply
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;Dear Professor Alvarez, I hope this email finds you well. I
            am writing to respectfully request a letter of recommendation for
            my college applications. I greatly enjoyed your class and learned
            a tremendous amount. I believe your recommendation would
            strengthen my application. Please let me know if this would be
            possible.&rdquo;
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Email B · Reply the next morning
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;Professor Alvarez, I was in your fifth-period US History
            class last spring. I wrote the paper on the 1919 steel strike that
            you asked me to present to the class. I am applying to five
            schools by November 15 and I am asking if you would write a
            recommendation. I can send you a one-page summary of what I would
            highlight and a list of the schools by Friday if you say
            yes.&rdquo;
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Email A reads like every rec-letter request the professor got this
        month. Email B opens by telling the professor exactly which student
        this is, exactly what work of theirs the professor already knows, and
        exactly what is being asked for and by when. The professor will reply
        to one of these. They will archive the other.
      </p>
      <p className="mt-4 text-text/90">
        AI cleaned up both. Only one had something to clean up. That is the
        lesson.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · 5.1" title="Cover letters earn the next sentence. Ask emails earn the next reply.">
      <p className="mt-6 text-text/90">
        Lesson 5.1 was about writing to a reader who has to pick one applicant.
        Lesson 5.2 is about writing to a reader who already knows who you are
        but has to decide whether to spend forty-five minutes writing something
        for you. A teacher, professor, or advisor with two hundred other
        students on the same deadline. Your email has to earn that reply.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Stakes in one sentence." />
        <ReviewItem n="02" q="The cut test: what do you add to any sentence to decide whether to cut it?" />
        <ReviewItem n="03" q="Three jobs of a high-stakes opening paragraph, in order." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The grounding line" title="Tell them who you are, what work of yours they already know, and what you want.">
      <Subhead n={1} title="The vocab for this lesson" />
      <p className="mt-4 text-text/90">
        One word holds the lesson together. Without it, ask emails read like
        the two hundred other requests in the professor&rsquo;s inbox this
        week. With it, they read like a specific student the professor can
        picture asking for a specific thing.
      </p>
      <VocabCard
        term="Grounding"
        definition="The piece of real-world evidence at the top of an ask email that tells the reader why this email came from you, specifically. Grounding is not flattery. It is the connection between something you actually did in their class or their program and the thing you are asking for. Without it, your request lands as noise. With it, the same request lands as a name and a face they already know."
        analogy="A weather report grounded in a sensor reading is testimony. A weather report grounded in nothing is a guess. AI uses grounding the same way: a sentence tied to a real source is trustworthy, a sentence floating free is not. Ask emails work on the same rule. The professor cares more about &ldquo;I wrote the paper on the 1919 steel strike&rdquo; than &ldquo;I really enjoyed your class.&rdquo;"
        memoryHook="Grounding answers: why me, why now, why this ask. No grounding, no reply."
        why="Most ask emails fail in the first two sentences because the writer leads with a compliment (&lsquo;I hope this email finds you well&rsquo;) instead of a piece of work the reader can place. The reader cannot picture the writer, so the request goes into the pile. Grounding is the move that turns a stranger into a named student in the reader&rsquo;s memory. It is also the term you will hear in any serious conversation about AI: ungrounded output is the failure mode, grounded output is the goal."
      />

      <Subhead n={2} title="The three-line opener" className="mt-12" />
      <p className="mt-4 text-text/90">
        Every ask email that gets a reply does three things in the first three
        lines. In this order.
      </p>
      <ThreeLineGrid />

      <Subhead n={3} title="What the ask looks like" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most ask emails never actually make an ask. They hint at one. The
        reader cannot act on a hint. They can act on a specific request. A
        good ask names what, by when, and what the reader has to do to say
        yes.
      </p>
      <AskGrid />

      <Subhead n={4} title="Where AI helps and where AI hurts" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is fluent in the language of student-to-teacher email because it
        has read thousands of them. That fluency is the problem.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The forward test" className="mt-12" />
      <p className="mt-4 text-text/90">
        Imagine your professor forwards your email to a colleague to ask
        &lsquo;do you remember this student?&rsquo; Does the colleague
        understand within five seconds who you are, which class you were in,
        and what you are asking for? If yes, the email survives the forward.
        If no, you wrote a compliment.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The check
        </p>
        <p className="mt-3 text-text/90">
          Read your first three lines and ask: if the reader forwarded this to
          another teacher who does not know me, would that teacher know who I
          am, which class or project connects us, and what I want them to do?
          If any of those answers takes more than one sentence to reach,
          rewrite the opener.
        </p>
      </div>
    </Section>
  );
}

const THREE_LINES = [
  {
    n: '01',
    name: 'Which student this is',
    body: 'One phrase. Which class, which section, which semester. Not your name in the sign-off. Not “I was in your class.” Something specific enough that the reader can pull up a mental image. Example: “I was in your fifth-period US History class last spring.”',
    accent: 'amber' as const,
  },
  {
    n: '02',
    name: 'Work the reader already knows',
    body: 'One sentence. A specific paper, project, question, or moment from the class that the reader will remember. This is the line that turns a stranger back into a student in the reader’s memory. Example: “I wrote the paper on the 1919 steel strike that you asked me to present to the class.”',
    accent: 'emerald' as const,
  },
  {
    n: '03',
    name: 'What you are asking, by when',
    body: 'One sentence. A concrete request with a date the reader can act on. Not “if possible.” Not “whenever you have time.” A specific ask and a specific deadline. Example: “I am applying to five schools by November 15 and I am asking if you would write a recommendation.”',
    accent: 'amber' as const,
  },
];

function ThreeLineGrid() {
  return (
    <div className="mt-6 space-y-4">
      {THREE_LINES.map((t) => {
        const isEm = t.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={t.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">Line {t.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{t.name}</h4>
            </div>
            <p className="mt-3 text-text/90">{t.body}</p>
          </div>
        );
      })}
    </div>
  );
}

const ASK_PAIRS = [
  {
    weak: 'Please let me know if a recommendation would be possible.',
    strong: 'Would you write a recommendation submitted through the Common App by November 15?',
    why: 'Possible is a maybe. A named platform and a date is a yes-or-no.',
  },
  {
    weak: 'I would love to discuss my project further whenever you have time.',
    strong: 'Could we meet for fifteen minutes this Thursday or Friday during your office hours to walk through the methods section?',
    why: 'Whenever is never. Two named days and a scoped topic fits into an existing calendar slot.',
  },
  {
    weak: 'I am very interested in getting involved with your research.',
    strong: 'Are you taking undergraduate researchers for the spring semester, and if so, may I send a one-page summary of what I would want to work on?',
    why: 'Interested is a feeling. Spring semester plus a one-page summary is a decision the professor can actually make.',
  },
];

function AskGrid() {
  return (
    <div className="mt-6 space-y-3">
      {ASK_PAIRS.map((p, i) => (
        <div key={i} className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr] rounded-lg border border-line bg-surface p-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-rose-300">Weak</p>
            <p className="mt-2 text-sm text-text/85 italic">{p.weak}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-300">Strong</p>
            <p className="mt-2 text-sm text-text/90">{p.strong}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Why</p>
            <p className="mt-2 text-sm text-text/80">{p.why}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Format: subject line, salutation, sign-off, paragraph order.',
      'Tone: keeping the email respectful when you are frustrated about a grade.',
      'Length: cutting a five-paragraph email to two.',
      'Structure: making sure the ask, the deadline, and the follow-up plan are all present.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'The grounding line: AI does not know which class you were actually in.',
      'The specific work: AI writes “I really enjoyed your class” instead of naming a real paper or moment.',
      'The ask: AI defaults to “if possible,” “whenever you have time,” “I would love to discuss.”',
      'The details AI cannot know: the paper title, the office-hours slot, the date something actually happened.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the grounding line" title="Three ask emails. Pick the opener that earns the next reply.">
      <p className="mt-6 text-text/90">
        Each drill shows two opening lines for the same email to the same
        reader. Pick the one that names grounding and earns the next reply.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <GroundingDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  reader: string;
  topic: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    reader: 'Email to a high school teacher',
    topic: 'Asking for a college recommendation letter',
    optionA:
      '“Ms. Patel, I hope you had a great summer. I am reaching out because I am starting my college applications and I would love it if you could write me a letter of recommendation. Your English class was one of my favorites and I really valued your feedback.”',
    optionB:
      '“Ms. Patel, I was in your junior English class last year and I wrote the essay on the sister in Everything I Never Told You that you asked me to read out loud. I am applying early action to four schools by November 1 and I am asking if you would write a recommendation. I have a one-page summary of what I would highlight if you say yes.”',
    correct: 'B',
    verdict:
      'Option B names the class, the essay, and the specific in-class moment the teacher will remember. Then it names the deadline, the number of schools, and offers the teacher a one-page summary that makes the letter easier to write. Option A is warm but generic. “One of my favorites” is what every rec-letter request says. The teacher cannot picture which student is writing.',
  },
  {
    n: '02',
    reader: 'Email to a college professor',
    topic: 'Asking to join their research group as an undergraduate',
    optionA:
      '“Professor Nguyen, I am very interested in your research and I would love to learn more about opportunities to get involved. I am a sophomore majoring in biology and I am passionate about the questions your lab explores. Please let me know if there is any way I could contribute.”',
    optionB:
      '“Professor Nguyen, I took your Intro to Neuroscience last spring and I wrote the final on the Nature paper you assigned about place cells in mice. I am a sophomore biology major and I am asking whether you are taking undergraduates for the spring semester. If yes, I can send a one-page summary of a question I would want to work on by next Friday.”',
    correct: 'B',
    verdict:
      'Option B names the class, the semester, and the specific paper the student engaged with in that class. Then it names the semester the student is asking about and offers a concrete follow-up on a specific date. Option A is enthusiastic but the professor cannot place the student and cannot act. “Any way I could contribute” puts the burden of making a plan on the professor. The strong version puts a proposal on the professor’s desk.',
  },
  {
    n: '03',
    reader: 'Email to an academic advisor',
    topic: 'Asking for help resolving a course-registration problem',
    optionA:
      '“Hi Dr. Ellis, I hope you are doing well. I am having some issues with my registration for next semester and I was wondering if we could meet at some point to discuss. Any time that works for you would be great. Thank you so much for your help.”',
    optionB:
      '“Dr. Ellis, I am the junior mechanical engineering student you helped with the Spanish minor override in September. The registrar is blocking me from adding ME 340 because the prerequisite ME 240 shows as incomplete, but I passed ME 240 in the spring. Could we meet for fifteen minutes Tuesday or Wednesday during your office hours to sort it out before add/drop closes on the twelfth?”',
    correct: 'B',
    verdict:
      'Option B names the specific past interaction the advisor will remember, the specific block in the registration system, the two specific courses involved, two named days, and the actual deadline that makes this urgent. The advisor can put this on the calendar and start pulling up the record before the meeting. Option A is polite but the advisor cannot prepare, cannot prioritize, and cannot even remember which student is writing.',
  },
];

function GroundingDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · {drill.reader}
      </p>
      <p className="mt-3 text-text/90">{drill.topic}</p>

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which opener earns the next reader?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block italic"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That opener does not name grounding. See the one that does below.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The opener with grounding' : 'Correct'} · Option {drill.correct}
            </p>
            <p
              className="mt-2 text-sm text-text/90"
              dangerouslySetInnerHTML={{ __html: drill.verdict }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Grounding in one sentence." answer="The reason the reader should remember that this email came from you: the connection between real work you did in their class or program and the thing you are asking for." />
        <QuickCheckItem n="Q2" q="The three lines of the ask-email opener, in order." answer="Which student this is. Work the reader already knows. What you are asking, by when." />
        <QuickCheckItem n="Q3" q="What turns a weak ask into a strong one?" answer="A weak ask names a feeling or leaves the plan to the reader. A strong ask names the specific thing, the deadline, and what the reader has to do to say yes." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on an ask email. Two things AI hurts." answer="Helps: format, tone, length, structure. Hurts: the grounding line, the specific work reference, the ask itself, the details AI cannot know about your actual class or project." />
        <QuickCheckItem n="Q5" q="What is the forward test?" answer="Imagine your reader forwards your email to another teacher who does not know you. If that teacher cannot tell within one sentence who is writing, which class connects you, and what you want, the opener needs to be rewritten." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The ask-email map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 5.2 · Lock card · The ask email
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Tell the reader which student this is, what work of yours they
          already know, and exactly what you want them to do by when.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <LockTile n="01" name="Line 1" body="Which student this is. Class, section, semester. Something the reader can pull up a mental image of." />
          <LockTile n="02" name="Line 2" body="Work the reader already knows. A specific paper, project, question, or moment from the class." />
          <LockTile n="03" name="Line 3" body="What you are asking, by when. A specific request with a specific deadline. No ‘if possible.’" />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The forward test
          </p>
          <p className="mt-2 text-text/90">
            If your reader forwards your email to another teacher who does not
            know you, can that teacher name who you are, which class connects
            you, and what you want? If not, rewrite the opener.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            Weak ask vs strong ask
          </p>
          <p className="mt-2 text-text/90">
            Weak: if possible, whenever you have time, I would love to discuss.
            Strong: a named deadline, a scoped topic, a specific platform, two
            named days, a one-page summary you will send by Friday. The reader
            can act on the second list.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Grounding turns a stranger back into a student the reader already
          knows.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 5.3
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The public post: writing strangers will read
          </h3>
          <p className="mt-3 text-sm text-text/85">
            LinkedIn, op-eds, public Substacks. Writing where the reader is not
            assigned and you have to earn the scroll-stop. Vocab: Persona
            conditioning. The two-second hook.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 5.2 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 5.1',
    'Grounding',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
